var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import accordionHostStyles from '@cof/fs-accordion-core/lib/accordion.host.lit.min.js';
import accordionStyles from '@cof/fs-accordion-core/lib/accordion.lit.min.js';
import { css, html, LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
import { FsAccordionItem } from './AccordionItem.js';
/**
 * `FsAccordion` implements the Financial Services implementation of the Accordion
 * component.
 * Be sure to consult with the design guidelines for the Accordion component to ensure proper state
 * usage, it is possible to force the Accordion component into an unsupported state.
 *
 * ## Installation
 * ### Install the node module:
 * ```bash
 * npm i @cof/fs-accordion-wc
 * ```
 *
 * ## Usage
 * To import the side-effectful registration of `<fs-accordion>` call
 *
 * ```javascript
 * import '@cof/fs-accordion-wc/fs-accordion.js';
 * ```
 *
 * To import the `FsAccordion` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { FsAccordion } from '@cof/fs-accordion-wc';
 * ```
 *
 * ## Anatomy
 * ### DOM Structure
 * ```html
 *   <fs-accordion
 *     icon-position="trailing"
 *     ?inset="false"
 *     size="medium"
 *   >
 *     <fs-accordion-item
 *       ?disabled="false"
 *       ?expanded="true"
 *     >
 *       <h2 slot="fs-accordion-item-title">Accordion item title</h2>
 *       <p slot="fs-accordion-item-content">Accordion item content</p>
 *     </fs-accordion-item>
 *   </fs-accordion>
 * ```
 *
 * @element fs-accordion
 * @slot - Content for fs-accordion. Must be a fs-accordion-item.
 */
export class FsAccordion extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * The position of the collapse / expand icon in child accordion items.
         *
         * Must be one of 'leading' or 'trailing'.
         *
         * @type FsAccordionIconPosition
         */
        this.iconPosition = null;
        /**
         * Whether or not the child accordion items have extra "inset" padding.
         *
         * @type boolean
         */
        this.inset = null;
        /**
         * The size of the child accordion items.
         *
         * Must be one of 'small', 'medium', or 'large'.
         *
         * @type FsAccordionSize
         */
        this.size = null;
    }
    static get styles() {
        return [
            css `
        :host {
          display: block;
        }
      `,
            accordionStyles,
            accordionHostStyles
        ];
    }
    _focusChild(child) {
        child.setAttribute('focused', 'true');
    }
    _unfocusChild(child) {
        child.removeAttribute('focused');
    }
    _focusFirstChild() {
        const children = this._getAccordionItemChildren();
        // Find the focusable children.
        const focusableChildrenIndexes = this._getFocusableAccordionItemChildrenIndexes(children, true);
        // If there are no focusable children, return.
        if (focusableChildrenIndexes.length === 0) {
            return;
        }
        // Un-focus the currently focused item.
        const focusedChildIndex = children.findIndex((child) => {
            return child.getAttribute('focused') != null;
        });
        if (focusedChildIndex !== -1) {
            this._unfocusChild(children[focusedChildIndex]);
        }
        // Focus the first focusable child.
        this._focusChild(children[focusableChildrenIndexes.shift()]);
    }
    _focusLastChild() {
        const children = this._getAccordionItemChildren();
        // Find the focusable children.
        const focusableChildrenIndexes = this._getFocusableAccordionItemChildrenIndexes(children, true);
        // If there are no focusable children, return.
        if (focusableChildrenIndexes.length === 0) {
            return;
        }
        // Un-focus the currently focused item.
        const focusedChildIndex = children.findIndex((child) => {
            return child.getAttribute('focused') != null;
        });
        if (focusedChildIndex !== -1) {
            this._unfocusChild(children[focusedChildIndex]);
        }
        // Focus the last focusable child.
        this._focusChild(children[focusableChildrenIndexes.pop()]);
    }
    _focusPreviousChild() {
        const children = this._getAccordionItemChildren();
        // Find the focusable children.
        const focusableChildrenIndexes = this._getFocusableAccordionItemChildrenIndexes(children);
        // If there are no focusable children, return.
        if (focusableChildrenIndexes.length === 0) {
            return;
        }
        // Un-focus the currently focused item.
        const focusedChildIndex = children.findIndex((child) => {
            return child.getAttribute('focused') != null;
        });
        if (focusedChildIndex !== -1) {
            this._unfocusChild(children[focusedChildIndex]);
        }
        // Try to focus the previous focusable child.
        const previousFocusableChildrenIndexes = focusableChildrenIndexes.filter((focusableChildIndex) => {
            return focusableChildIndex < focusedChildIndex;
        });
        if (previousFocusableChildrenIndexes.length > 0) {
            this._focusChild(children[previousFocusableChildrenIndexes.pop()]);
            return;
        }
        // Otherwise, wrap around.
        this._focusChild(children[focusableChildrenIndexes.pop()]);
    }
    _focusNextChild() {
        const children = this._getAccordionItemChildren();
        // Find the focusable children.
        const focusableChildrenIndexes = this._getFocusableAccordionItemChildrenIndexes(children);
        // If there are no focusable children, return.
        if (focusableChildrenIndexes.length === 0) {
            return;
        }
        // Un-focus the currently focused item.
        const focusedChildIndex = children.findIndex((child) => {
            return child.getAttribute('focused') != null;
        });
        if (focusedChildIndex !== -1) {
            this._unfocusChild(children[focusedChildIndex]);
        }
        // Try to focus the next focusable child.
        const upcomingFocusableChildrenIndexes = focusableChildrenIndexes.filter((focusableChildIndex) => {
            return focusableChildIndex > focusedChildIndex;
        });
        if (upcomingFocusableChildrenIndexes.length > 0) {
            this._focusChild(children[upcomingFocusableChildrenIndexes.shift()]);
            return;
        }
        // Otherwise, wrap around.
        this._focusChild(children[focusableChildrenIndexes.shift()]);
    }
    _getAccordionItemChildren(slotElement) {
        const currentSlotElement = slotElement || this.slotElement;
        const children = currentSlotElement.assignedNodes();
        return children.filter((child) => {
            return child instanceof FsAccordionItem;
        });
    }
    _getFocusableAccordionItemChildrenIndexes(accordionItemChildren, includeFocusedChildren = false) {
        return accordionItemChildren
            .map((accordionItemChild, index) => {
            return accordionItemChild.disabled || (!includeFocusedChildren && accordionItemChild.focused) ? null : index;
        })
            .filter((index) => {
            return index != null;
        })
            .sort();
    }
    _handleKeyDown(event) {
        switch (event.key) {
            case 'ArrowUp': {
                this._focusPreviousChild();
                break;
            }
            case 'ArrowDown': {
                this._focusNextChild();
                break;
            }
            case 'Home': {
                this._focusFirstChild();
                break;
            }
            case 'End': {
                this._focusLastChild();
                break;
            }
            default: {
                return;
            }
        }
    }
    _handleSlotChange() {
        const children = this._getAccordionItemChildren(this.slotElement);
        children.forEach((child, index) => {
            // Propagate properties to children accordion items.
            child.setAttribute('icon-position', this.iconPosition);
            child.setAttribute('index', index.toString());
            child.setAttribute('size', this.size);
            if (this.inset) {
                child.setAttribute('inset', 'true');
            }
            else {
                child.removeAttribute('inset');
            }
        });
    }
    /**
     * Render the component.
     *
     * @returns TemplateResult
     */
    render() {
        return html `
      <div class="fs-accordion" @keydown=${this._handleKeyDown}>
        <slot @slotchange="${this._handleSlotChange}"></slot>
      </div>
    `;
    }
    /**
     * Lifecycle hook called when the component has been updated.
     *
     * @protected
     */
    updated() {
        this._handleSlotChange();
    }
}
/** The element's canonical name for use with safe define */
FsAccordion.canonicalName = 'accordion';
__decorate([
    property({ attribute: 'icon-position', type: String })
], FsAccordion.prototype, "iconPosition", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsAccordion.prototype, "inset", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsAccordion.prototype, "size", void 0);
__decorate([
    query('slot')
], FsAccordion.prototype, "slotElement", void 0);
//# sourceMappingURL=Accordion.js.map